import React, { useEffect, useRef } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { GoogleOAuthProvider, MomentListener, useGoogleOneTapLogin } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID, GOOGLE_ONE_TAP_DEBUG } from '@src/env';
import {
  ClickTarget,
  EventProperties,
  ImpressionElement,
  TrackingLocation,
  click,
  impression,
} from '@tracking';
import { GOOGLE_PROVIDER, createLeadWithOauth } from '@utils/backend-api/oauth';

import { ONE_TAP_NEW_LEAD_KEY } from './constants';

interface GoogleOneTapProps {
  analytics?: EventProperties;
  isGdprRequired: boolean;
}

interface GoogleOneTapInnerProps extends GoogleOneTapProps {
  onNotDisplayed: (reason: string) => void;
  trackEvent: (properties: object) => void;
}

const DISABLED_PATHS = ['/login', '/subscribe'];

// Keep in sync with OneTap::LEAD_TYPE in app/models/concerns/one_tap.rb
const LEAD_TYPE = 'onetap';

// Google reports a reader closing the prompt as a skipped moment with one of these reasons.
// Other reasons (auto_cancel, issuing_failed) are not the reader's choice.
const DISMISS_SKIPPED_REASONS = ['user_cancel', 'tap_outside'];

// console.debug is not shimmed in the SSR bundle (mini_racer), so use log/error.
const debugLog = (...args: unknown[]) => {
  if (GOOGLE_ONE_TAP_DEBUG) console.log('[OneTap]', ...args);
};
const debugError = (...args: unknown[]) => {
  if (GOOGLE_ONE_TAP_DEBUG) console.error('[OneTap]', ...args);
};

// These status methods are deprecated under FedCM and GSI logs a warning whenever a listener is
// registered, so whether Google still reports display and dismissal has to be confirmed in a browser.
const logPromptMoment: MomentListener = (notification) => {
  debugLog('promptMomentNotification:', {
    isDisplayMoment: notification.isDisplayMoment(),
    isDisplayed: notification.isDisplayed(),
    isNotDisplayed: notification.isNotDisplayed(),
    getNotDisplayedReason: notification.isNotDisplayed()
      ? notification.getNotDisplayedReason()
      : null,
    isSkippedMoment: notification.isSkippedMoment(),
    getSkippedReason: notification.isSkippedMoment() ? notification.getSkippedReason() : null,
    isDismissedMoment: notification.isDismissedMoment(),
    getDismissedReason: notification.isDismissedMoment() ? notification.getDismissedReason() : null,
  });
};

function GoogleOneTapInner({
  analytics,
  isGdprRequired,
  onNotDisplayed,
  trackEvent,
}: GoogleOneTapInnerProps) {
  const isDisabled = DISABLED_PATHS.includes(window.location.pathname);
  const isImpressionTracked = useRef(false);

  debugLog('GoogleOneTapInner rendered. props:', { isGdprRequired });
  debugLog('Current path:', window.location.pathname, '| isDisabled:', isDisabled);

  useEffect(() => {
    debugLog('GoogleOneTapInner mounted.');
    debugLog('window.google present:', !!(window as any).google);
    debugLog('window.google.accounts present:', !!(window as any).google?.accounts);
    debugLog('google.accounts.id present:', !!(window as any).google?.accounts?.id);
  }, []);

  // The denominator for "should have seen the prompt": every page where it was set up to show.
  useEffect(() => {
    if (isDisabled) return;
    trackEvent(impression({ impressionElement: ImpressionElement.OneTapPromptEligible }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePromptMoment: MomentListener = (notification) => {
    if (GOOGLE_ONE_TAP_DEBUG) logPromptMoment(notification);

    // Tracking must never break the prompt, and these methods may not be supported under FedCM.
    try {
      if (notification.isDisplayed() && !isImpressionTracked.current) {
        isImpressionTracked.current = true;
        trackEvent(impression({ impressionElement: ImpressionElement.OneTapPrompt }));
      } else if (notification.isNotDisplayed()) {
        onNotDisplayed(notification.getNotDisplayedReason());
      } else if (notification.isSkippedMoment()) {
        const skippedReason = notification.getSkippedReason();
        if (DISMISS_SKIPPED_REASONS.includes(skippedReason)) {
          trackEvent(click({ clickTarget: ClickTarget.Dismiss, value: skippedReason }));
        } else {
          // auto_cancel / issuing_failed: the reader never saw or chose anything.
          onNotDisplayed(skippedReason);
        }
      }
    } catch (trackingError) {
      debugError('prompt moment tracking failed:', trackingError);
    }
  };

  useGoogleOneTapLogin({
    disabled: isDisabled,
    use_fedcm_for_prompt: true,
    cancel_on_tap_outside: true,
    auto_select: false,
    promptMomentNotification: handlePromptMoment,
    onSuccess: async (credentialResponse) => {
      debugLog('onSuccess fired. credential present:', !!credentialResponse.credential);
      try {
        const payload = {
          id_token: credentialResponse.credential,
          provider: GOOGLE_PROVIDER,
          // The page type (article, homepage, ...) so leads can be split by where they signed in.
          form_location: analytics?.page,
          lead_type: LEAD_TYPE,
          opt_in_required: isGdprRequired,
          declined_opt_in: isGdprRequired,
          // Path + query only: browsers never send the hash to the server, and it is restored below.
          page: window.location.pathname + window.location.search,
        };
        debugLog('createLeadWithOauth payload:', payload);
        const response = await createLeadWithOauth(payload);
        debugLog('createLeadWithOauth response:', response);

        trackEvent(
          click({ clickTarget: ClickTarget.OneTapSignin, is_new_user: response.isNewUser })
        );

        // Only new leads see the welcome banner. sessionStorage can be unavailable (private
        // mode, blocked storage); the sign-in must still complete.
        if (response.isNewUser) {
          try {
            window.sessionStorage.setItem(ONE_TAP_NEW_LEAD_KEY, '1');
          } catch (storageError) {
            debugError('sessionStorage unavailable:', storageError);
          }
        }

        // Existing leads get the page they were on as `location`. New leads get null (no
        // redirect_to_on_success is sent), so stay on the current page and reload it signed in.
        if (response.location) {
          // The server's location has the path and query but never the hash; keep the reader's.
          const { hash } = window.location;
          window.location.href =
            hash && !response.location.includes('#') ? response.location + hash : response.location;
        } else {
          window.location.reload();
        }
      } catch (error) {
        // Silent fail unless debugging — One Tap is ambient, errors should not interrupt reading
        debugError('createLeadWithOauth failed:', error);
      }
    },
    onError: () => {
      debugError('useGoogleOneTapLogin onError fired');
    },
  });

  return null;
}

export default function GoogleOneTap(props: GoogleOneTapProps) {
  const hydrated = useServerSideRendering();
  const isNotDisplayedTracked = useRef(false);
  const { trackEvent } = useEventTracking({
    ...props.analytics,
    location: TrackingLocation.OneTapPrompt,
  });

  // Why the prompt did not show (Google's reason, or script_load_error). Once per page.
  const onNotDisplayed = (reason: string) => {
    if (isNotDisplayedTracked.current) return;
    isNotDisplayedTracked.current = true;
    trackEvent(
      impression({
        context: reason,
        impressionElement: ImpressionElement.OneTapPromptNotDisplayed,
      })
    );
  };

  debugLog(
    'GoogleOneTap outer rendered. hydrated:',
    hydrated,
    '| GOOGLE_CLIENT_ID:',
    GOOGLE_CLIENT_ID ? `set (${GOOGLE_CLIENT_ID.slice(0, 12)}...)` : 'MISSING/EMPTY'
  );

  if (!hydrated) return null;

  return (
    <GoogleOAuthProvider
      clientId={GOOGLE_CLIENT_ID}
      onScriptLoadError={() => onNotDisplayed('script_load_error')}
    >
      <GoogleOneTapInner {...props} onNotDisplayed={onNotDisplayed} trackEvent={trackEvent} />
    </GoogleOAuthProvider>
  );
}
